import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Skeleton, TextField } from '@mui/material'
import { ModalWrapper } from '@microservices/wiskey-react-components'
import { InternalIdInput } from '@library'

import { TextSettings, type ExternalTextSettings, type GetTextParamsHandle, type TextSettingsStateType } from '@components/TextSettings'
import { useFetchStyleSettingByIdQuery, useCreateOrUpdateStyleSettingMutation } from '@redux/api/styleSetting.api'
import { BORDER_TYPE } from '@constants'

type StyleSettingsDialogForm = {
  internalId: string
  title: string
}

export type StyleSettingsDialogProps = {
  isShow: boolean
  styleSettingId?: string | number
  onClose: () => void
  onSaved?: (styleSetting: any) => void
}

export const StyleSettingsDialog: FC<StyleSettingsDialogProps> = ({
  isShow,
  styleSettingId,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation()

  const isEdit = styleSettingId != null && String(styleSettingId) !== ''
  const methods = useForm<StyleSettingsDialogForm>({
    defaultValues: { internalId: '', title: '' },
  })
  const {
    handleSubmit,
    reset,
    formState: { isDirty },
  } = methods

  const textSettingsRef = useRef<GetTextParamsHandle>(null)

  const { data: styleSettingById, isLoading: isLoadingById } = useFetchStyleSettingByIdQuery(
    { id: String(styleSettingId) },
    { skip: !isEdit }
  )
  const [createOrUpdateStyleSetting, { isLoading: isSaving }] =
    useCreateOrUpdateStyleSettingMutation()

  const initialTextSettings: TextSettingsStateType | undefined = useMemo(() => {
    if (!styleSettingById?.settingText) return undefined
    const st = styleSettingById.settingText
    return {
      fontFamily: { id: st.fontFamily, label: st.fontFamily },
      fontColor: st.fontColor,
      alignment: st.alignment,
      fontSize: `${st.fontSize}`,
      padding: `${st.padding ?? 0}`,
      formats: [st.isBold ? 'bold' : '', st.isItalic ? 'italic' : ''].filter(Boolean) as any,
      bgFontColor: styleSettingById.background,
      borderColor: styleSettingById.settingBorder?.color,
    }
  }, [styleSettingById])

  const [isDirtyTextSettings, setDirtyTextSettings] = useState(false)
  const handleTextExternalDirty = (value: ExternalTextSettings) => {
    if (!initialTextSettings) {
      setDirtyTextSettings(true)
      return
    }
    setDirtyTextSettings(JSON.stringify(value) !== JSON.stringify(initialTextSettings))
  }

  useEffect(() => {
    if (!isShow) return
    if (isEdit && styleSettingById) {
      reset({
        title: styleSettingById.title,
        internalId: styleSettingById.internalId,
      })
    }
    if (!isEdit) {
      reset({ title: '', internalId: '' })
    }
  }, [isShow, isEdit, styleSettingById, reset])

  const handleClose = () => {
    if ((isDirty || isDirtyTextSettings) && !confirm(t('notifications.leave'))) {
      return
    }
    onClose()
  }

  const handleSave = handleSubmit(async (data: StyleSettingsDialogForm) => {
    if (!textSettingsRef.current) return

    const {
      fontFamily,
      fontSize,
      fontColor,
      alignment,
      padding,
      isBold,
      isItalic,
      bgFontColor,
      borderColor,
    } = textSettingsRef.current.getTextParams()

    try {
      const saved = await createOrUpdateStyleSetting({
        ...(isEdit ? { id: String(styleSettingId) } : {}),
        title: data.title,
        internalId: data.internalId,
        background: bgFontColor ? bgFontColor : 'none',
        settingText: {
          fontFamily,
          fontSize: fontSize as string,
          fontColor: fontColor as string,
          alignment,
          ...(padding != null ? { padding: Number(padding) } : {}),
          isBold: isBold === 'true',
          isItalic: isItalic === 'true',
        },
        settingBorder: {
          // TODO: убрать/согласовать тип бордера
          type: BORDER_TYPE.ROUNDED_SQUARE,
          color: borderColor as string,
        },
      }).unwrap()

      onSaved?.(saved)
      onClose()
    } catch {
      // commonAPI handles error
    }
  })

  return (
    <ModalWrapper
      isShow={isShow}
      title={
        isEdit
          ? t('styleSettings.title.edit') ?? 'Edit style setting'
          : t('styleSettings.title.create') ?? 'Create style setting'
      }
      disabledSave={isSaving}
      hideDivider
      onClose={handleClose}
      onSave={handleSave}
      containerStyle={{
        px: 3,
        py: 2,
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          pr: 1,
          mr: -1,
        }}
      >
        <FormProvider {...methods}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              columnGap: 2,
              rowGap: 0,
              mt: 2,
              mb: 2,
            }}
          >
            <Controller
              name='internalId'
              control={methods.control}
              rules={{
                required: true,
                validate: value => value.trim().length !== 0,
              }}
              render={({ field, fieldState }) => (
                <InternalIdInput
                  {...field}
                  fullWidth
                  disabled={isEdit || isLoadingById}
                  label={t(`styleSettings.internalId.label`)}
                  placeholder={t(`styleSettings.internalId.placeholder`)}
                  size='small'
                  error={!!fieldState.error}
                  helperText={fieldState.error ? ' ' : undefined}
                />
              )}
            />
            <Controller
              name='title'
              control={methods.control}
              rules={{
                required: true,
                validate: value => value.trim().length !== 0,
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  fullWidth
                  disabled={isLoadingById}
                  label={t(`styleSettings.title.styleSetting`)}
                  placeholder={t(`styleSettings.title.styleSetting`)}
                  size='small'
                  error={!!fieldState.error}
                  helperText={fieldState.error ? ' ' : undefined}
                />
              )}
            />
          </Box>

          {isLoadingById ? (
            <Box>
              <Skeleton height={60} />
              <Skeleton height={60} />
              <Skeleton height={60} />
            </Box>
          ) : (
            <TextSettings
              ref={textSettingsRef}
              borderSettings={true}
              paddingSettings={true}
              initialTextState={initialTextSettings}
              isEdit={true}
              labelPlacement='top'
              labelText={t('parameters.visual.columnText')}
              onSetExternalDirty={handleTextExternalDirty}
            />
          )}
        </FormProvider>
      </Box>
    </ModalWrapper>
  )
}

